/**
 * Config Generator
 *
 * Converts UI state to BacktestRequest JSON payload for the backend.
 * This is the UI -> Backend direction of the bidirectional config system.
 * The counterpart (configParser) will handle Backend -> UI direction.
 */

import { Leg, OrGroups as UIOrGroups } from "../types/flowchart";
import { ConfiguredSignal } from "../../../../config/signals";
import { ConfiguredDynamicCondition } from "../../../../config/signals";
import { TradeCondition as UITradeCondition } from "../flowchart/TradeConditions";
import logger from '../../../../logging/logger';
import {
  BacktestRequest,
  BackendOrGroups,
  BackendAndGroup,
  SignalSpec,
  Param,
  Parameters,
  BackendTradeCondition,
  BackendDynamicCondition,
  Direction,
  OptionType,
  LegSignals,
} from "./types";

/**
 * Signals and trade conditions that have leg_idx parameter
 */
const SIGNALS_WITH_LEG_IDX = ["dollar_return_cap", "percentage_return_cap", "dte_signal"];
const DYNAMIC_CONDITIONS_WITH_LEG_IDX = ["delta_hedge_quantity"];

function toBackendLegIdx(value: Param): Param {
  if (value === undefined || value === null || value === "") return value;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value - 1;
  }

  if (typeof value === "string") {
    if (value.includes(",")) {
      return value
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part !== "")
        .map((part) => Number(part))
        .filter((num) => Number.isFinite(num))
        .map((num) => String(num - 1))
        .join(",");
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(parsed - 1) : value;
  }

  return value;
}

function normalizeLegIdxParam(params: Parameters): Parameters {
  if (!Object.prototype.hasOwnProperty.call(params, "leg_idx")) {
    return params;
  }

  return {
    ...params,
    leg_idx: toBackendLegIdx(params.leg_idx),
  };
}

function normalizeIntradayMaParams(signalType: string, params: Parameters): Parameters {
  if (signalType !== "intraday_ma_crossover") {
    return params;
  }

  const frequency = params.frequency;
  if (!frequency || typeof frequency !== "object" || Array.isArray(frequency)) {
    return params;
  }

  const unit = (frequency as { unit?: unknown }).unit;
  if (unit !== "seconds" && unit !== "minutes" && unit !== "hours") {
    return params;
  }

  return {
    ...params,
    frequency: {
      every: 1,
      unit,
    },
  };
}

/**
 * Apply default leg_idx if not provided
 * - For strategy-level: default to all leg numbers (comma-separated)
 * - For leg-level: default to that specific leg number
 */
function applyLegIdxDefault(
  signalType: string,
  params: Parameters,
  context: { isStrategyLevel: boolean; legIndex?: number; totalLegs: number }
): Parameters {
  // Check if this signal/condition has leg_idx parameter
  const hasLegIdx = SIGNALS_WITH_LEG_IDX.includes(signalType) ||
                    DYNAMIC_CONDITIONS_WITH_LEG_IDX.includes(signalType);

  if (!hasLegIdx) {
    return params;
  }

  // If leg_idx is already provided, don't override
  if (params.leg_idx !== undefined && params.leg_idx !== null && params.leg_idx !== "") {
    return params;
  }

  // Apply default based on context
  const defaultValue = context.isStrategyLevel
    ? Array.from({ length: context.totalLegs }, (_, i) => i + 1).join(",") // "1,2,3,..."
    : String((context.legIndex ?? 0) + 1); // "1" or specific leg number

  return {
    ...params,
    leg_idx: defaultValue,
  };
}

/**
 * Convert a ConfiguredSignal to backend SignalSpec format
 * SignalSpec is Record<signalType, Parameters>
 *
 * @param signal - The configured signal to convert
 * @param context - Context for applying defaults (strategy-level vs leg-level)
 */
function convertSignal(
  signal: ConfiguredSignal,
  context?: { isStrategyLevel: boolean; legIndex?: number; totalLegs: number }
): SignalSpec | null {
  // Skip empty signals (no type selected)
  if (!signal.type) {
    return null;
  }

  // Convert params to backend format
  let params: Parameters = {};
  for (const [key, value] of Object.entries(signal.params)) {
    if (value === undefined || value === null || value === "") continue;
    // Arrays (like leg_idx) → comma-separated string
    if (Array.isArray(value)) {
      params[key] = value.join(",");
    } else if (typeof value === "number" || typeof value === "boolean") {
      params[key] = value;
    } else if (typeof value === "object") {
      // Structured params (e.g. frequency: { every, unit }) pass through as-is
      params[key] = value as Parameters[string];
    } else {
      params[key] = String(value);
    }
  }

  // Apply leg_idx defaults if context is provided
  if (context) {
    params = applyLegIdxDefault(signal.type, params, context);
  }
  params = normalizeLegIdxParam(params);
  params = normalizeIntradayMaParams(signal.type, params);

  return { [signal.type]: params };
}

/**
 * Convert UI OrGroups (ConfiguredSignal[][]) to backend OrGroups format
 *
 * @param uiOrGroups - The UI OrGroups to convert
 * @param context - Context for applying defaults (strategy-level vs leg-level)
 */
function convertOrGroups(
  uiOrGroups: UIOrGroups,
  context?: { isStrategyLevel: boolean; legIndex?: number; totalLegs: number }
): BackendOrGroups {
  const result: BackendOrGroups = [];

  for (const andGroup of uiOrGroups) {
    const backendAndGroup: BackendAndGroup = [];

    for (const signal of andGroup) {
      const converted = convertSignal(signal, context);
      if (converted) {
        backendAndGroup.push(converted);
      }
    }

    // Only add non-empty AND groups
    if (backendAndGroup.length > 0) {
      result.push(backendAndGroup);
    }
  }

  return result;
}

/**
 * Convert UI dynamic conditions to backend format
 *
 * @param conditions - The dynamic conditions to convert
 * @param context - Context for applying defaults (always leg-level for dynamic conditions)
 */
function convertDynamicConditions(
  conditions: ConfiguredDynamicCondition[] | undefined,
  context?: { isStrategyLevel: boolean; legIndex?: number; totalLegs: number }
): BackendDynamicCondition | undefined {
  if (!conditions || conditions.length === 0) {
    return undefined;
  }

  const result: BackendDynamicCondition = [];

  for (const condition of conditions) {
    if (!condition.type) continue;

    let params: Parameters = {};
    for (const [key, value] of Object.entries(condition.params)) {
      if (value === undefined || value === null || value === "") continue;
      if (Array.isArray(value)) {
        params[key] = value.join(",");
      } else if (typeof value === "number" || typeof value === "boolean") {
        params[key] = value;
      } else if (typeof value === "object") {
        params[key] = value as Parameters[string];
      } else {
        params[key] = String(value);
      }
    }

    // Apply leg_idx defaults if context is provided
    if (context) {
      params = applyLegIdxDefault(condition.type, params, context);
    }
    params = normalizeLegIdxParam(params);

    result.push({ [condition.type]: params });
  }

  return result.length > 0 ? result : undefined;
}

/**
 * Convert direction from UI format to backend format
 */
function convertDirection(dir: "long" | "short" | undefined): Direction {
  return dir === "short" ? "Short" : "Long";
}

/**
 * Convert option type from UI format to backend format
 */
function convertOptionType(type: "Call" | "Put" | undefined): OptionType | undefined {
  return type;
}

/**
 * Normalize strategy-level symbols for request payload
 */
function normalizeSymbols(symbolBank: string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const raw of symbolBank) {
    const symbol = (raw || "").trim().toUpperCase();
    if (!symbol || seen.has(symbol)) continue;
    seen.add(symbol);
    normalized.push(symbol);
  }

  return normalized;
}

/**
 * Extract greek values from greekConditions array
 */
function extractGreekValues(greekConditions?: Array<{
  id: string;
  greek: "delta" | "gamma" | "theta" | "vega" | "rho" | "strike" | "iv" | "Dynamic" | "";
  greekValue?: number;
  dynamicGreek?: string;
}>): {
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
  strike?: number;
  implied_vol?: number;
} {
  const result: {
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    rho?: number;
    strike?: number;
    implied_vol?: number;
  } = {};

  if (!greekConditions) return result;

  for (const gc of greekConditions) {
    if (gc.greekValue === undefined || gc.greekValue === null) continue;

    switch (gc.greek) {
      case "delta":
        result.delta = gc.greekValue;
        break;
      case "gamma":
        result.gamma = gc.greekValue;
        break;
      case "theta":
        result.theta = gc.greekValue;
        break;
      case "vega":
        result.vega = gc.greekValue;
        break;
      case "rho":
        result.rho = gc.greekValue;
        break;
      case "strike":
        result.strike = gc.greekValue;
        break;
      case "iv":
        result.implied_vol = gc.greekValue;
        break;
    }
  }

  return result;
}

/**
 * Convert a UI TradeCondition to backend TradeCondition format
 *
 * @param condition - The trade condition to convert
 * @param defaultSymbol - Default symbol if not specified
 * @param legIndex - The index of the leg (for applying leg_idx defaults)
 * @param totalLegs - Total number of legs in the strategy
 */
function convertTradeCondition(
  condition: UITradeCondition,
  defaultSymbol?: string,
  legIndex?: number,
  totalLegs?: number
): BackendTradeCondition {
  const symbol = condition.symbol || defaultSymbol || "";
  const direction = convertDirection(condition.direction);

  // Dynamic conditions are always leg-level
  const dynamicContext = legIndex !== undefined && totalLegs !== undefined
    ? { isStrategyLevel: false, legIndex, totalLegs }
    : undefined;

  const dynamic = condition.dynamicEnabled
    ? convertDynamicConditions(condition.dynamicConditions, dynamicContext)
    : undefined;

  if (condition.instrumentType === "Option") {
    // Extract greek values from greekConditions array
    const greekValues = extractGreekValues(condition.greekConditions);

    // For Put options, ensure delta is negative (backend expects -1 to 0)
    // UI always displays delta as 0 to 1, so we negate here if needed
    let delta = greekValues.delta;
    if (condition.optionType === "Put" && delta !== undefined && delta > 0) {
      delta = -delta;
    }

    return {
      Option: {
        symbol: symbol || undefined,
        instrument: convertOptionType(condition.optionType),
        strike: greekValues.strike,
        expiration: condition.expiration,
        dte: condition.dte,
        implied_vol: greekValues.implied_vol,
        delta,
        gamma: greekValues.gamma,
        theta: greekValues.theta,
        vega: greekValues.vega,
        rho: greekValues.rho,
        quantity: condition.quantity,
        direction,
        dynamic,
      },
    };
  }

  // Default to Stock
  return {
    Stock: {
      symbol,
      quantity: condition.quantity,
      direction,
      dynamic,
    },
  };
}

/**
 * UI State interface matching BacktestConfig component state
 */
export interface UIBacktestState {
  legs: Leg[];
  symbolBank: string[];
  strategyName: string;
  startDate: string;
  endDate: string;
  intradayEnabled?: boolean;
  strategySignalsEnabled: boolean;
  strategyEntryRules: UIOrGroups;
  strategyExitRules: UIOrGroups;
}

/**
 * Main generator function: Convert UI state to BacktestRequest
 */
export function generateBacktestRequest(state: UIBacktestState): BacktestRequest {
  const totalLegs = state.legs.length;
  const strategySymbols = normalizeSymbols(state.symbolBank);

  // Convert strategy-level signals with strategy-level context
  const strategyContext = { isStrategyLevel: true, totalLegs };
  const userEntrySignals: BackendOrGroups = convertOrGroups(state.strategyEntryRules, strategyContext);

  const intradayEnabled = state.intradayEnabled === true;
  // Non-intraday strategies default to on_exit when no entry is configured.
  // Intraday strategies intentionally keep entry empty (warning-only UX).
  const DEFAULT_ENTRY: BackendOrGroups = [[{ on_exit: {} }]];
  const entrySignals: BackendOrGroups = userEntrySignals.length > 0
    ? userEntrySignals
    : (intradayEnabled ? [] : DEFAULT_ENTRY);

  const exitSignals: BackendOrGroups = convertOrGroups(state.strategyExitRules, strategyContext);

  // Build strategy_info from legs
  const strategyInfo: Record<string, BackendTradeCondition> = {};
  const signalMap: Record<string, LegSignals> = {};

  // When a single strategy-level symbol is configured, use it as the leg-symbol fallback.
  // This keeps execution paths that rely on leg symbols working for single-symbol strategies.
  const defaultSymbol = strategySymbols.length === 1 ? strategySymbols[0] : undefined;

  for (let legIndex = 0; legIndex < state.legs.length; legIndex++) {
    const leg = state.legs[legIndex];
    const legKey = String(legIndex);

    // Each leg has one trade condition (first in the array)
    // The trade condition can have multiple dynamic conditions in a list
    if (leg.tradeConditions.length > 0) {
      const condition = leg.tradeConditions[0];
      strategyInfo[legKey] = convertTradeCondition(condition, defaultSymbol, legIndex, totalLegs);
    }

    // Add leg-specific signals to signal_map with leg-level context
    const legContext = { isStrategyLevel: false, legIndex, totalLegs };
    const legEntrySignals = convertOrGroups(leg.entryRules, legContext);
    const legExitSignals = convertOrGroups(leg.exitRules, legContext);

    // Only add to signal_map if there are signals
    if (legEntrySignals.length > 0 || legExitSignals.length > 0) {
      signalMap[legKey] = {
        entry_signals: legEntrySignals.length > 0 ? legEntrySignals : undefined,
        exit_signals: legExitSignals.length > 0 ? legExitSignals : undefined,
      };
    }
  }

  const result = {
    symbols: strategySymbols,
    entry_signals: entrySignals,
    exit_signals: exitSignals,
    strategy_info: strategyInfo,
    start_date: state.startDate || undefined,
    end_date: state.endDate || undefined,
    signal_map: signalMap,
  };

  logger.info('configGenerator: Generated config', { config: JSON.stringify(result, null, 2) });

  return result;
}
